/**
 * 获取安全周报明细表数据 Hook
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { getDetailTable } from '@op/services/weeklyReport';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import type { IDetailTableRow, IDetailTableResponse } from '@op/services/weeklyReport';

interface IUseDetailTableDataOptions {
  autoFetch?: boolean; // 是否自动请求
}

interface IUseDetailTableDataResult {
  data: IDetailTableRow[];
  total: number;
  summary?: IDetailTableResponse['summary'];
  loading: boolean;
  empty: boolean;
  error: boolean;
  fetchData: (params: any) => Promise<any>;
}

/**
 * 获取明细表数据
 * @param params - 请求参数
 * @param options - 配置选项
 * @returns 数据、状态和刷新方法
 */
export const useDetailTableData = (
  params?: any,
  options?: IUseDetailTableDataOptions,
): IUseDetailTableDataResult => {
  const { autoFetch = true } = options || {};
  const [response, setResponse] = useState<IDetailTableResponse | null>(null);

  const { loading, error, wrapperCancelRequest } = useCancelRequestHook<IDetailTableResponse>({
    clear: () => setResponse(null),
  });

  const fetchData = useCallback(
    (requestParams: any) => {
      return wrapperCancelRequest(getDetailTable, requestParams)
        .then((res) => {
          if (res) {
            setResponse(res);
            return res;
          }
        })
        .catch((err) => {
          console.error('获取明细表数据失败:', err);
        });
    },
    [],
  );

  // 自动请求：params 存在且包含必需的时间参数时才请求
  useEffect(() => {
    if (!params || !autoFetch) return;
    if (!params.start_time || !params.end_time) return;
    fetchData(params);
  }, [params, autoFetch]);

  const tableData = useMemo(() => {
    return response?.table_data || [];
  }, [response]);

  const empty = useMemo(
    () => !loading && tableData.length === 0,
    [loading, tableData],
  );

  return {
    data: tableData,
    total: response?.total ?? 0,
    summary: response?.summary,
    loading,
    empty,
    error,
    fetchData,
  };
};

export default useDetailTableData;
